/**
 * HUD colour + type theme. Owned by `ui`.
 *
 * ARCHITECTURE.md §10 puts the WORLD palette in src/materials/palette.js (owned by `look`).
 * These are interface colours only — they never touch a THREE.Material.
 *
 * Every hex below is either sampled straight out of reference/palette/palette.md
 * (Crazy Taxi HUD section, PowerWash HUD section, cone orange section) or is a derived
 * ramp built from those anchors. Nothing is invented.
 */

export const C = Object.freeze({
  // Crazy Taxi HUD yellow. palette.md: sampled #FAFC1A, authored #FFF200.
  yellow: '#FFF200',
  yellowHi: '#FFFCA6',
  yellowMid: '#FFD400',
  yellowDeep: '#E8960B',

  // Outline black on all HUD type. palette.md #0C120D.
  ink: '#0C120D',
  /** Bevel foot of an ink-faced numeral — a value ramp on `ink`, not a separate grey. */
  inkSoft: '#333333',
  ink70: 'rgba(12,18,13,0.70)',
  ink45: 'rgba(12,18,13,0.45)',
  ink22: 'rgba(12,18,13,0.22)',

  // Marquee / plate.
  cream: '#F4F2EA',        // authored warm white (sampled #E8EBEE)
  creamShade: '#D8D4C6',
  white: '#FFFFFF',
  frameRed: '#A53639',     // marquee frame + end bulbs
  /** Frame ramp — a bevel on `frameRed`, so the signboard turns without a new hue. */
  frameRedHi: '#C4585B',
  frameRedLow: '#7A2427',
  /** §2.3 "dark maroon digits on the same cream tiles" — the multiplier token. */
  maroon: '#7A1E20',
  hotRed: '#E01E12',
  deepRed: '#A5150C',
  slowRed: '#C50904',
  // palette.md §1: the Crazy Taxi timer goes near-pure red at low time, and it BLINKS.
  timerRed: '#EA020A',
  timerRedShade: '#A00509',

  // Fare plate navy.
  navy: '#101428',
  navyMid: '#2B2D52',
  navySoft: '#3A3E6E',
  silver: '#B5B9C8',

  // Directional arrow green (3-D extruded chevron in the reference).
  green: '#0BCE38',
  greenHi: '#17E653',
  greenDark: '#08AC32',
  greenInk: '#053F16',

  // Ville de Montréal works-department orange — our identity colour. Cone orange ramp.
  orange: '#FC6523',
  orangeHi: '#F88849',
  orangeMid: '#D74611',
  orangeShade: '#AB2419',
  orangeDeep: '#6D291C',

  // PowerWash HUD anchors, used for the completion language.
  pwNavy: '#0C3352',
  pwPlate: '#103049',
  pwBadge: '#2F5373',
  pwTrack: '#1D4163',
  pwAzure: '#0070CE',

  // Asphalt ramp for the before/after payoff thumbnails.
  patchTar: '#0E0D0C',
  patchCut: '#6E6A66',
  patchGrit: '#9A968F',
  roadLit: '#A0A0A1',
  roadMid: '#8E8A85',
  roadShade: '#646465',
  patchWet: '#1F1D1C',
  patchFresh: '#3A3836',
  patchCured: '#8C8C8C',
  holeVoid: '#242830',
  aggregate: '#DBDBDC',
  lanePaint: '#F1F3F0',
});

/**
 * ---------------------------------------------------------------- SCENE palette
 *
 * The destination inset draws a small illustrated view of the objective. Round 11's blind
 * critic made it a FATAL: "a flat clip-art vector placeholder in a frame full of 3D art…
 * it shares zero art direction with the world behind it."
 *
 * These are therefore not invented. Every value below was sampled off the shipped
 * `harness/out-final/combo-hud.png` with a mode-of-region histogram, so the inset is painted
 * out of the same palette as the geometry it sits on top of. If `look` re-grades the world,
 * re-sample rather than eyeballing a correction.
 *
 *   sky top #387BFF · sky low #A8CBFF · cloud band #97B5F7
 *   tan facade #A69C82 (shade #877C63) · red brick #B48A6A (shade #98755A)
 *   window glass #082739 · staircase #030302 · road #A4A6A5 · tree #5A9906 / #84A74A
 *
 * `stone*` has no sample because the world carries no church yet; it is a limestone ramp
 * built at the same value separation as the brick ramp so the two read as one light.
 */
export const S = Object.freeze({
  skyTop: '#387BFF',
  skyMid: '#79A9EE',
  skyLow: '#A8CBFF',
  cloud: '#C9DDFF',

  brickTan: '#A69C82',
  brickTanLit: '#BDB296',
  brickTanShade: '#877C63',
  brickRed: '#B48A6A',
  brickRedLit: '#C79A76',
  brickRedShade: '#98755A',
  /** Limestone for the church — cooler and lighter than the brick, same value spread. */
  stone: '#B9B4A6',
  stoneLit: '#D2CDBE',
  stoneShade: '#948F82',
  /** Copper spire — the Plateau church roof, verdigris. */
  copper: '#4E8C78',
  copperLit: '#69AB93',

  glass: '#082739',
  glassLit: '#14405C',
  sill: '#C9C2AC',
  cornice: '#8E856E',
  stair: '#0B0B0A',

  roadPlan: '#A4A6A5',
  roadPlanFar: '#BEC0BE',
  sidewalkPlan: '#B6B5B0',
  kerb: '#8E8D89',
  lanePaintScene: '#F1F5F0',
  laneYellow: '#E8B21F',

  leafLit: '#84A74A',
  leaf: '#5A9906',
  leafDark: '#3D6A05',
  trunk: '#4A3728',

  // Winter and night re-grades of the same scene, one ramp each.
  winterSky: '#E4EBF1',
  winterSkyLow: '#C2CBD2',
  winterGround: '#DCE2E6',
  nightSky: '#141824',
  nightSkyLow: '#3A2A16',
  nightGround: '#4A3A1C',
  sodium: '#F5C46A',
});

/**
 * Display family. ARCHITECTURE.md N1 — NO font files. We read the stack index.html already
 * declares (--ui-display) and fall back to the same list literally if the var is missing.
 */
const DISPLAY_FALLBACK =
  "Haettenschweiler, 'Arial Narrow Bold', 'Arial Narrow', Impact, 'Franklin Gothic Bold', 'Franklin Gothic Medium', sans-serif";
const SANS_FALLBACK = "system-ui, -apple-system, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif";

let _display = null;
let _sans = null;

function readVar(name, fallback) {
  try {
    const v = getComputedStyle(document.documentElement).getPropertyValue(name).trim();
    return v || fallback;
  } catch {
    return fallback;
  }
}

export function displayFamily() {
  if (_display === null) _display = readVar('--ui-display', DISPLAY_FALLBACK);
  return _display;
}

export function sansFamily() {
  if (_sans === null) _sans = readVar('--ui-sans', SANS_FALLBACK);
  return _sans;
}

/** Reset cached family lookups (harness reset). */
export function resetFamilies() { _display = null; _sans = null; }

/** rgba() from a #rrggbb plus alpha, without allocating a colour object. */
const _hexCache = new Map();
export function alpha(hex, a) {
  let rgb = _hexCache.get(hex);
  if (!rgb) {
    const n = parseInt(hex.slice(1), 16);
    rgb = [(n >> 16) & 255, (n >> 8) & 255, n & 255];
    _hexCache.set(hex, rgb);
  }
  return `rgba(${rgb[0]},${rgb[1]},${rgb[2]},${a})`;
}

/** Mix two #rrggbb toward t in [0,1]. Used for warn/critical timer ramps. */
export function mix(a, b, t) {
  const na = parseInt(a.slice(1), 16), nb = parseInt(b.slice(1), 16);
  const r = Math.round(((na >> 16) & 255) + (((nb >> 16) & 255) - ((na >> 16) & 255)) * t);
  const g = Math.round(((na >> 8) & 255) + (((nb >> 8) & 255) - ((na >> 8) & 255)) * t);
  const bl = Math.round((na & 255) + ((nb & 255) - (na & 255)) * t);
  return `rgb(${r},${g},${bl})`;
}

export default C;
